import {
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
} from '@mui/material';
import { useState } from 'react';
import styled from 'styled-components';
import {
  useDisassociateTagFromDeviceMutation,
  useGetDeviceTagsQuery,
} from '../../gql/generated';
import { TagKnown } from '../../Pages/ManageTags/TagsTable';
import { ModalContent } from './ModalContent';

interface DisassociateDeviceTagPanelProps {
  tag: TagKnown;
  onClose: () => void;
}

const StyledStack = styled(Stack)`
  max-height: 320px;
  width: 100%;
  overflow: auto;
`;

export const DisassociateDeviceTagPanel = (
  props: DisassociateDeviceTagPanelProps,
) => {
  const [checked, setChecked] = useState(['']);
  const { tag, onClose } = props;
  const {
    data: getDeviceTagsData,
    loading: getDeviceTagsLoading,
    error,
  } = useGetDeviceTagsQuery({
    variables: {
      input: {
        tagId: tag.tagId,
        limit: 150,
      },
    },
    fetchPolicy: 'no-cache',
  });
  const [disassociateTagWithDevice, { loading: disassociateTagLoading }] =
    useDisassociateTagFromDeviceMutation();

  const deviceTags = (getDeviceTagsData?.getDeviceTags?.results ?? []).filter(
    Boolean,
  ) as {
    tagId: string;
    serialNumber: string;
  }[];

  const handleToggle = (value: string | undefined | null) => () => {
    if (value) {
      const currentIndex = checked.indexOf(value);
      const newChecked = [...checked];
      if (currentIndex === -1) {
        newChecked.push(value);
      } else {
        newChecked.splice(currentIndex, 1);
      }
      setChecked(newChecked);
    }
  };

  const handleDissociateTag = async () => {
    const filteredChecked = checked.filter((c) => c !== '');
    if (tag.tagId) {
      const associations = filteredChecked.map(async (sn) => {
        await disassociateTagWithDevice({
          variables: {
            input: {
              tagId: tag.tagId,
              serialNumber: sn,
            },
          },
        });
      });
      await Promise.all(associations);
    }
    onClose();
  };

  return (
    <div>
      <ModalContent
        title="Dissociate Device(s) from Tag"
        loading={getDeviceTagsLoading}
        error={error}
        handleClose={onClose}
        handleSubmit={handleDissociateTag}
        buttonLabels={{
          loading: 'Disassociating Tag(s)...',
          create: 'Disassociate Tag(s)',
        }}
        submitDisabled={getDeviceTagsLoading || disassociateTagLoading}
      >
        <StyledStack>
          <List sx={{ width: '100%' }}>
            {deviceTags.map((value) => {
              const labelId = `checkbox-list-label-${value.serialNumber}`;
              return (
                <ListItem key={value.serialNumber} disablePadding>
                  <ListItemButton
                    role={undefined}
                    onClick={handleToggle(value.serialNumber)}
                    dense
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={checked.includes(value.serialNumber)}
                        tabIndex={-1}
                        disableRipple
                        inputProps={{ 'aria-labelledby': labelId }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      id={labelId}
                      primary={`${value.serialNumber}`}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </StyledStack>
      </ModalContent>
    </div>
  );
};
